// src/hooks/useAddress.jsx
import { useState, useEffect, useRef, useCallback } from "react";
import { useSalesContext } from "../contexts/SalesContext";
import { useSupaContext } from "../contexts/SupaContext";
import { useGoogleMaps } from "./useGoogleMaps";
import { cleanStringNumber, sendLeadToWebhook } from "../scripts/generalScript";

// Constantes de entorno
const NO_COVERAGE_WEBHOOK_URL = process.env.REACT_APP_NO_COVERAGE_WEBHOOK_URL;

// CONSTANTES DE ZONAS DE DELIVERY
const DELIVERY_ZONES = {
  1: { price: 0, label: "Entrega gratis", color: "green" },
  2: { price: 3500, label: "Entrega $3.500", color: "yellow" },
  3: { price: 7000, label: "Entrega $7.000", color: "orange" },
};

export function useAddress(polygonData, shouldInitialize = true) {
  // --- Hook de Google Maps ---
  const {
    isMapsApiLoaded,
    isMapsInitialized,
    autocompleteService,
    placesService,
    validateLocation,
    fetchStaticMapImage,
  } = useGoogleMaps(polygonData, shouldInitialize);

  // --- Contextos ---
  const {
    name,
    phone,
    email,
    setSelectedAddress,
    addressDetails,
    setAddressDetails,
    selectedCoordinates,
    setSelectedCoordinates,
    setImageAddress,
    selectedSugs,
    selectedRefills,
    setDeliveryFee,
    isCoordsLoading,
    setIsCoordsLoading,
    isValid,
    setIsValid,
    // Nuevos estados para zonas
    selectedZone,
    setSelectedZone,
    setIsChangingAddress
  } = useSalesContext();

  const { createAddressAfterCreateUser, addPreRegistration } = useSupaContext();

  // --- Estados Locales ---
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [noResults, setNoResults] = useState(false);
  const [mapImage, setMapImage] = useState(null);
  const [suggestionSelected, setSuggestionSelected] = useState(false);
  const [addressHasDetails, setAddressHasDetails] = useState(true);
  const [currentZone, setCurrentZone] = useState(null); // Estado local para la zona actual
  const [localDeliveryFee, setLocalDeliveryFee] = useState(0);

  // --- Referencias ---
  const searchTimeoutRef = useRef(null);

  // --- Función para calcular delivery fee basado en zona ---
  const calculateDeliveryFee = useCallback((zone) => {
    if (!zone || zone < 1 || zone > 3) return 0;
    return DELIVERY_ZONES[zone]?.price || 0;
  }, []);

  // --- Función para obtener información de zona ---
  const getZoneInfo = useCallback((zone) => {
    if (!zone || zone < 1 || zone > 3) return null;
    return DELIVERY_ZONES[zone];
  }, []);

  // --- Funciones de Negocio ---
  const handleInvalidLocation = useCallback(
    async (coordinates, selectedSuggestion) => {
      console.log("useAddress: Manejando ubicación fuera de cobertura...");

      // Limpiar estados relacionados con zona
      setCurrentZone(null);
      setSelectedZone && setSelectedZone(null);
      setLocalDeliveryFee(0);
      setDeliveryFee(0);

      if (NO_COVERAGE_WEBHOOK_URL && name && phone) {
        try {
          const leadData = {
            name: name || "N/A",
            phone: cleanStringNumber(phone) || "N/A",
            email: email || "N/A",
            location: selectedSuggestion || "Dirección no válida",
            coordinates: coordinates
              ? `${coordinates.lat}, ${coordinates.lng}`
              : "N/A",
            selected_sugs: selectedSugs || 0,
            selected_refills: selectedRefills || 0,
            timestamp: new Date().toISOString(),
          };

          // await sendLeadToWebhook(NO_COVERAGE_WEBHOOK_URL, leadData);
          console.log("useAddress: Lead de ubicación inválida enviado.");
        } catch (error) {
          console.error(
            "useAddress: Error enviando lead de ubicación inválida:",
            error
          );
        }
      }
    },
    [
      name,
      phone,
      email,
      selectedSugs,
      selectedRefills,
      setDeliveryFee,
      setSelectedZone,
    ]
  );

  const handleCorrectLocationLead = useCallback(
    async (coordinates, selectedSuggestion, zone) => {
      const leadData = {
        name: name || "N/A",
        phone: cleanStringNumber(phone) || "N/A",
        email: email || "N/A",
        location: selectedSuggestion || "Dirección válida",
        coordinates: coordinates
          ? `${coordinates.lat}, ${coordinates.lng}`
          : "N/A",
        zone: zone || "N/A",
        delivery_fee: calculateDeliveryFee(zone),
        selected_sugs: selectedSugs || 0,
        selected_refills: selectedRefills || 0,
        timestamp: new Date().toISOString(),
      };

      try {
        await createAddressAfterCreateUser?.(leadData);
        await addPreRegistration?.(leadData);
        console.log("useAddress: Lead de ubicación válida procesado.");
      } catch (error) {
        console.error(
          "useAddress: Error procesando lead de ubicación válida:",
          error
        );
      }
    },
    [
      name,
      phone,
      email,
      selectedSugs,
      selectedRefills,
      createAddressAfterCreateUser,
      addPreRegistration,
      calculateDeliveryFee,
    ]
  );

  // --- Función: Validar Ubicación ---
  const handleValidateLocation = useCallback(
    async (coordinates, suggestionDesc) => {
      if (!isMapsInitialized || !coordinates) {
        console.warn(
          "useAddress: Maps no inicializado o coordenadas inválidas para validar."
        );
        setIsValid(false);
        setCurrentZone(null);
        setSelectedZone && setSelectedZone(null);
        return false;
      }

      const validationResult = validateLocation(coordinates);
      const { isValid: isLocValid, zone, distanceKm } = validationResult;

      setIsValid(isLocValid);
      setCurrentZone(zone);
      setSelectedZone && setSelectedZone(zone);

      console.log(
        `useAddress: Validación - Válida: ${isLocValid}, Zona: ${zone}, Distancia: ${distanceKm}km`
      );

      if (!isLocValid) {
        console.log("useAddress: Ubicación fuera de cobertura, manejando...");
        await handleInvalidLocation(coordinates, suggestionDesc);
      } else {
        console.log(
          `useAddress: Ubicación válida en zona ${zone}, procesando lead...`
        );
        await handleCorrectLocationLead(coordinates, suggestionDesc, zone);
      }

      return isLocValid;
    },
    [
      isMapsInitialized,
      validateLocation,
      handleInvalidLocation,
      handleCorrectLocationLead,
      setIsValid,
      setSelectedZone,
    ]
  );

  // --- Función: Buscar Sugerencias ---
  const fetchSuggestions = useCallback(
    (input) => {
      if (!input.trim() || !isMapsInitialized || !autocompleteService) {
        setSuggestions([]);
        setNoResults(false);
        setIsCoordsLoading(false);
        return;
      }

      console.log("useAddress: Buscando sugerencias para:", input);

      // Límites geográficos de Barranquilla
      const bounds = new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(10.8, -74.9),
        new window.google.maps.LatLng(11.1, -74.7)
      );

      const request = {
        input,
        componentRestrictions: { country: "CO" },
        locationRestriction: bounds,
        types: ["geocode", "establishment"],
      };

      autocompleteService.getPlacePredictions(request, (results, status) => {
        let predictions = [];
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          Array.isArray(results)
        ) {
          predictions = results;
        } else if (
          status !== window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS
        ) {
          console.warn(`useAddress: Estado de Places API: ${status}`);
        }

        // Filtrado específico de zonas permitidas
        const zonaPermitida = ["barranquilla", "puerto colombia"];
        const zonaProhibida = [
          "soledad",
          "malambo",
          "sabanalarga",
          "baranoa",
          "tubará",
          "tubara",
          "galapa",
        ];

        const filtered = predictions.filter((p) => {
          if (!p?.description) return false;
          const desc = p.description.toLowerCase();
          const permitido = zonaPermitida.some((z) => desc.includes(z));
          const prohibido = zonaProhibida.some((z) => desc.includes(z));
          return permitido && !prohibido;
        });

        setSuggestions(filtered);
        setNoResults(filtered.length === 0);
        setIsCoordsLoading(false);
      });
    },
    [input, isMapsInitialized, autocompleteService, setIsCoordsLoading]
  );

  // --- Función: Manejar Clic en Sugerencia ---
  const handleSuggestionClick = useCallback(
    async (suggestion) => {
      if (!suggestion?.description || !suggestion.place_id) return;
      if (!isMapsInitialized || !placesService) {
        console.error("useAddress: Places service no está listo.");
        setIsCoordsLoading(false);
        return;
      }

      const description = suggestion.description;
      console.log("useAddress: Sugerencia seleccionada:", description);

      // Actualizar UI inmediatamente
      setInput(description);
      setSelectedAddress(description);
      setSuggestions([]);
      setSuggestionSelected(true);
      setIsCoordsLoading(true);

      // Limpiar estados previos
      setIsValid(null);
      setCurrentZone(null);
      setSelectedZone && setSelectedZone(null);
      setMapImage(null);
      setImageAddress(null);
      setLocalDeliveryFee(0);
      setDeliveryFee(0);

      placesService.getDetails(
        {
          placeId: suggestion.place_id,
          fields: ["geometry", "name", "formatted_address"],
        },
        async (place, status) => {
          let obtainedCoords = null;
          let locationIsValid = false;
          let finalZone = null;

          try {
            if (
              status === window.google.maps.places.PlacesServiceStatus.OK &&
              place?.geometry?.location
            ) {
              obtainedCoords = {
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
              };

              console.log("useAddress: Coordenadas obtenidas:", obtainedCoords);
              setSelectedCoordinates(obtainedCoords);

              // Validar ubicación y obtener zona
              locationIsValid = await handleValidateLocation(
                obtainedCoords,
                description
              );

              if (locationIsValid && currentZone) {
                finalZone = currentZone;

                // Obtener imagen del mapa
                const imgResult = await fetchStaticMapImage(obtainedCoords);
                if (imgResult?.blob) {
                  setMapImage(imgResult.blob);
                  setImageAddress(imgResult.blob);
                }

                // Calcular y establecer tarifa de delivery
                const deliveryFee = calculateDeliveryFee(finalZone);
                setLocalDeliveryFee(deliveryFee);
                setDeliveryFee(deliveryFee);

                console.log(
                  `useAddress: Zona ${finalZone} - Tarifa: $${deliveryFee}`
                );
              }
            } else {
              console.error(
                `useAddress: Error al obtener detalles (${suggestion.place_id}):`,
                status
              );
              setIsValid(false);
              setSelectedCoordinates(null);
              setCurrentZone(null);
              setSelectedZone && setSelectedZone(null);
            }
          } catch (error) {
            console.error(
              "useAddress: Error en callback de getDetails:",
              error
            );
            setIsValid(false);
            setSelectedCoordinates(null);
            setCurrentZone(null);
            setSelectedZone && setSelectedZone(null);
          } finally {
            setIsCoordsLoading(false);
          }
        }
      );
    },
    [
      isMapsInitialized,
      placesService,
      setSelectedAddress,
      setSelectedCoordinates,
      setIsCoordsLoading,
      handleValidateLocation,
      fetchStaticMapImage,
      setImageAddress,
      setDeliveryFee,
      setSelectedZone,
      currentZone,
      calculateDeliveryFee,
    ]
  );

  // --- Efecto: Debounce para búsqueda de sugerencias ---
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (input.trim() && !suggestionSelected) {
      setIsCoordsLoading(true);
      searchTimeoutRef.current = setTimeout(() => {
        fetchSuggestions(input);
      }, 500);
    } else if (!input.trim()) {
      setSuggestions([]);
      setNoResults(false);
      setIsCoordsLoading(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [input, suggestionSelected, fetchSuggestions, setIsCoordsLoading]);

  // En useAddress.jsx - MODIFICAR el efecto de actualización del mapa y tarifa:

  useEffect(() => {
    // Agregar debounce para evitar múltiples actualizaciones
    const updateTimer = setTimeout(() => {
      const updateMapAndFee = async () => {
        if (
          selectedCoordinates?.lat &&
          selectedCoordinates?.lng &&
          isMapsInitialized &&
          !isCoordsLoading
        ) {
          const validationResult = validateLocation(selectedCoordinates);
          const { isValid: isLocValid, zone } = validationResult;

          // Solo actualizar si algo cambió
          if (
            isValid !== isLocValid ||
            currentZone !== zone ||
            !mapImage ||
            localDeliveryFee !== calculateDeliveryFee(zone)
          ) {
            setIsValid(isLocValid);
            setCurrentZone(zone);
            setSelectedZone && setSelectedZone(zone);

            if (isLocValid && zone) {
              console.log(
                "useAddress (Effect): Actualizando imagen y tarifa para zona",
                zone
              );

              const imgResult = await fetchStaticMapImage(selectedCoordinates);
              if (imgResult?.blob) {
                setMapImage(imgResult.blob);
                setImageAddress(imgResult.blob);
              }

              const fee = calculateDeliveryFee(zone);
              setLocalDeliveryFee(fee);

              // IMPORTANTE: Solo actualizar el delivery fee si es diferente
              // Esto evita el reset a 0 durante transiciones
              setDeliveryFee((prevFee) => {
                if (prevFee !== fee) {
                  return fee;
                }
                return prevFee;
              });
            } else {
              console.log(
                "useAddress (Effect): Coordenadas inválidas, limpiando estados"
              );
              setMapImage(null);
              setImageAddress(null);
              setLocalDeliveryFee(0);
              setDeliveryFee(0);
              setCurrentZone(null);
              setSelectedZone && setSelectedZone(null);
            }
          }
        } else if (!selectedCoordinates) {
          // Solo limpiar si realmente NO hay coordenadas
          // No durante transiciones
          setMapImage(null);
          setImageAddress(null);
          setLocalDeliveryFee(0);
          setDeliveryFee(0);
          setCurrentZone(null);
          setSelectedZone && setSelectedZone(null);
          setIsValid(null);
        }
      };

      updateMapAndFee();
    }, 300); // Debounce de 300ms

    return () => clearTimeout(updateTimer);
  }, [
    selectedCoordinates,
    isMapsInitialized,
    validateLocation,
    fetchStaticMapImage,
    setImageAddress,
    setDeliveryFee,
    setSelectedZone,
    isValid,
    currentZone,
    isCoordsLoading,
    calculateDeliveryFee,
    mapImage,
    localDeliveryFee,
  ]);

  // --- Handlers de UI ---
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInput(value);

    if (suggestionSelected) setSuggestionSelected(false);

    if (value !== input) {
      // Resetear estados cuando el input cambia
      setSelectedAddress("");
      setSelectedCoordinates(null);
      setIsValid(null);
      setAddressDetails("");
      setMapImage(null);
      setImageAddress(null);
      setLocalDeliveryFee(0);
      setDeliveryFee(0);
      setCurrentZone(null);
      setSelectedZone && setSelectedZone(null);
      setNoResults(false);
      setSuggestions([]);
    }
  };

  // TAMBIÉN MODIFICAR la función clearAddress para usar el estado de transición:
  const clearAddress = () => {
    // Indicar que estamos en transición si el contexto lo permite
    if (setIsChangingAddress) {
      setIsChangingAddress(true);
    }

    setInput("");
    setSelectedAddress("");
    setAddressDetails("");
    setSuggestions([]);
    setIsValid(null);
    setSelectedCoordinates(null);
    setNoResults(false);
    setIsCoordsLoading(false);
    setSuggestionSelected(false);
    setMapImage(null);
    setImageAddress(null);
    setLocalDeliveryFee(0);
    setDeliveryFee(0);
    setCurrentZone(null);
    setSelectedZone && setSelectedZone(null);

    // Finalizar transición después de un momento
    if (setIsChangingAddress) {
      setTimeout(() => {
        setIsChangingAddress(false);
      }, 300);
    }
  };

  const handleAddressDetailsChange = (e) => setAddressDetails(e.target.value);

  const handleAddressHasDetailsChange = (checked) => {
    setAddressHasDetails(checked);
    if (!checked) setAddressDetails("");
  };

  // --- Valores Devueltos ---
  return {
    // Estados básicos
    input,
    setInput,
    suggestions,
    isValid,
    noResults,
    mapImage,
    addressHasDetails,
    setAddressHasDetails,

    // Estados de zona (NUEVO)
    currentZone,
    selectedZone,
    deliveryFee: localDeliveryFee,

    // Funciones de zona (NUEVO)
    getZoneInfo,
    calculateDeliveryFee,
    DELIVERY_ZONES,

    // Handlers
    handleInputChange,
    handleSuggestionClick,
    clearAddress,
    handleAddressDetailsChange,
    handleAddressHasDetailsChange,

    // Estados de carga
    isMapsApiLoading: !isMapsApiLoaded,
    isMapsInitializing: isMapsApiLoaded && !isMapsInitialized,
  };
}
